import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';

interface ActivityLogEntry {
  id: number;
  type: string;
  courseId: number | null;
  courseName: string | null;
  levelId: number | null;
  levelName: string | null;
  createdAt: string;
  userName: string;
  userSurname: string;
}

interface TopPerformer {
  id: number;
  name: string;
  surname: string;
  totalPoints: number;
}

interface OrganizationPerson extends TopPerformer {
  coursesStarted: number;
  levelsCompleted: number;
  averageTestScore: number | null;
}

interface CourseUserSummary {
  id: number;
  name: string;
  surname: string;
}

interface CourseStats {
  id: number;
  name: string;
  totalUsers: number;
  totalPoints: number;
  averagePoints: number;
  eligibleUsers: number;
  startedBy: CourseUserSummary[];
}

interface CourseLevelDetail {
  levelId: number;
  levelName: string;
  levelNumber: number;
  usersAtLevel: CourseUserSummary[];
}

interface OrganizationCourseDetail {
  courseId: number;
  courseName: string;
  notStarted: CourseUserSummary[];
  levels: CourseLevelDetail[];
}

interface OrganizationStatsOverview {
  organization: { id: number; name: string };
  totalUsers: number;
  totalCourses: number;
  totalPoints: number;
  recentActivity: ActivityLogEntry[];
  topPerformers: TopPerformer[];
  courseStats: CourseStats[];
}

interface PaginatedActivity {
  activities: ActivityLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedCourses {
  courses: CourseStats[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedPeople {
  people: OrganizationPerson[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface StaffPointsEntry {
  rank: number;
  id: number;
  name: string;
  surname: string;
  totalPoints: number;
}

interface PaginatedStaffPoints {
  staffPoints: StaffPointsEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface StaffPointsFilters {
  includeTests: boolean;
  startDate?: string;
  endDate?: string;
  courseIds?: number[];
  departmentIds?: number[];
}

async function fetchOrganizationStats(
  organizationId: number,
): Promise<OrganizationStatsOverview> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_OVERVIEW(organizationId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch organization stats');
  }

  return response.json();
}

async function fetchOrganizationActivity(
  organizationId: number,
  page: number,
  limit: number,
): Promise<PaginatedActivity> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_ACTIVITY(organizationId, page, limit),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || 'Failed to fetch organization activity',
    );
  }

  return response.json();
}

async function fetchOrganizationCourses(
  organizationId: number,
  page: number,
  limit: number,
): Promise<PaginatedCourses> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_COURSES(organizationId, page, limit),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || 'Failed to fetch organization courses',
    );
  }

  return response.json();
}

async function fetchOrganizationPeople(
  organizationId: number,
  page: number,
  limit: number,
): Promise<PaginatedPeople> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_PEOPLE(organizationId, page, limit),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch organization people');
  }

  return response.json();
}

async function fetchTopPerformers(
  organizationId: number,
  limit: number,
  includeTests: boolean,
  startDate?: string,
  endDate?: string,
): Promise<TopPerformer[]> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_TOP_PERFORMERS(
      organizationId,
      limit,
      includeTests,
      startDate,
      endDate,
    ),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch top performers');
  }

  return response.json();
}

async function fetchOrganizationCourseDetail(
  organizationId: number,
  courseId: number,
): Promise<OrganizationCourseDetail> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_COURSE_DETAIL(organizationId, courseId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch course detail');
  }

  return response.json();
}

export function useOrganizationStats(organizationId: number | undefined) {
  return useQuery({
    queryKey: ['organization', organizationId, 'stats'],
    queryFn: () => fetchOrganizationStats(organizationId!),
    enabled: !!organizationId,
  });
}

export function useTopPerformers(
  organizationId: number | undefined,
  limit: number = 7,
  includeTests: boolean = false,
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: [
      'organization',
      organizationId,
      'top-performers',
      limit,
      includeTests,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchTopPerformers(
        organizationId!,
        limit,
        includeTests,
        startDate,
        endDate,
      ),
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });
}

export function useOrganizationActivity(
  organizationId: number | undefined,
  page: number,
  limit: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'activity', page, limit],
    queryFn: () => fetchOrganizationActivity(organizationId!, page, limit),
    enabled: !!organizationId && enabled,
  });
}

export function useOrganizationCourses(
  organizationId: number | undefined,
  page: number,
  limit: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'courses', page, limit],
    queryFn: () => fetchOrganizationCourses(organizationId!, page, limit),
    enabled: !!organizationId && enabled,
  });
}

async function fetchStaffPoints(
  organizationId: number,
  page: number,
  limit: number,
  filters: StaffPointsFilters,
): Promise<PaginatedStaffPoints> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_POINTS(
      organizationId,
      page,
      limit,
      filters.includeTests,
      filters.startDate,
      filters.endDate,
      filters.courseIds,
      filters.departmentIds,
    ),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch staff points');
  }

  return response.json();
}

export function useOrganizationStaffPoints(
  organizationId: number | undefined,
  page: number,
  limit: number,
  filters: StaffPointsFilters,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [
      'organization',
      organizationId,
      'staff-points',
      page,
      limit,
      filters,
    ],
    queryFn: () => fetchStaffPoints(organizationId!, page, limit, filters),
    enabled: !!organizationId && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useOrganizationPeople(
  organizationId: number | undefined,
  page: number,
  limit: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'people', page, limit],
    queryFn: () => fetchOrganizationPeople(organizationId!, page, limit),
    enabled: !!organizationId && enabled,
  });
}

export function useOrganizationCourseDetail(
  organizationId: number | undefined,
  courseId: number | undefined,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'courses', courseId, 'detail'],
    queryFn: () => fetchOrganizationCourseDetail(organizationId!, courseId!),
    enabled: !!organizationId && !!courseId && enabled,
  });
}

export type {
  OrganizationStatsOverview,
  ActivityLogEntry,
  TopPerformer,
  OrganizationPerson,
  StaffPointsEntry,
  PaginatedStaffPoints,
  StaffPointsFilters,
  CourseStats,
  CourseUserSummary,
  OrganizationCourseDetail,
  CourseLevelDetail,
  PaginatedActivity,
  PaginatedCourses,
  PaginatedPeople,
};
